import React from 'react'
import { Link } from 'react-router-dom'
import { useFormContext, Controller } from 'react-hook-form'
import clsx from 'clsx'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Typography from '@material-ui/core/Typography'
import { useAuth } from 'lib/auth'
import { PhoneField, EmailFieldWithSuggestions } from 'components/form-util'
import InviteLockableTextField from '../../invite-lockable-text-field'
import { useRegistrationInvite } from '../../registration-invite'
import useStyles from './styles'

const SignedInUser = ({ user }) => {
  const classes = useStyles()

  return (
    <div className={classes.signedInCard}>
      <div>
        <Typography variant="body1" style={{ fontWeight: 700 }}>
          {user.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {user.email}
        </Typography>
      </div>
      <Button component={Link} to="/logout" color="primary" size="small">
        Not You?
      </Button>
    </div>
  )
}

const ContactFields = () => {
  const classes = useStyles()
  const { register, errors, control, watch, setValue } = useFormContext()
  const firstParticipantType = watch('participants[0].type')
  const useFirstParticipantContactInformation = watch(
    'useFirstParticipantContactInformation',
  )
  const { user, isConsumer } = useAuth()
  const {
    isValid: inviteIsValid,
    firstName: inviteFirstName,
    lastName: inviteLastName,
  } = useRegistrationInvite()
  const isAuthenticatedConsumer = user && isConsumer

  // uncheck the "Same as Participant #1" checkbox whenever the first participant isn't an adult
  if (
    firstParticipantType !== 'Adult' &&
    useFirstParticipantContactInformation
  ) {
    setValue('useFirstParticipantContactInformation', false)
  }

  const hideIdentityFields =
    isAuthenticatedConsumer || useFirstParticipantContactInformation
  const hidePhone = useFirstParticipantContactInformation

  return (
    <section className={classes.section}>
      <Typography variant="h6" component="h2" className={classes.sectionTitle}>
        Contact information
      </Typography>
      <p className={classes.sectionHint}>
        We&apos;ll send your registration confirmation here.
      </p>
      {isAuthenticatedConsumer && <SignedInUser user={user} />}
      <FormControlLabel
        className={classes.sameAsFirst}
        style={{
          display:
            isAuthenticatedConsumer || firstParticipantType !== 'Adult'
              ? 'none'
              : undefined,
        }}
        control={
          <Controller
            name="useFirstParticipantContactInformation"
            control={control}
            render={props => (
              <Checkbox
                {...props}
                color="primary"
                checked={props.value}
                onChange={e => props.onChange(e.target.checked)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            )}
          />
        }
        label="Same as Participant #1"
      />
      <div className={classes.fieldGrid}>
        <InviteLockableTextField
          id="contact-first-name"
          name="contact.firstName"
          locked={inviteIsValid}
          lockedValue={inviteFirstName}
          error={!!errors?.contact?.firstName}
          helperText={errors?.contact?.firstName?.message}
          label="First name"
          autoComplete="given-name"
          className={clsx(hideIdentityFields && classes.hidden)}
          required
        />
        <InviteLockableTextField
          id="contact-last-name"
          name="contact.lastName"
          locked={inviteIsValid}
          lockedValue={inviteLastName}
          error={!!errors?.contact?.lastName}
          helperText={errors?.contact?.lastName?.message}
          label="Last name"
          autoComplete="family-name"
          className={clsx(hideIdentityFields && classes.hidden)}
          required
        />
        <div className={clsx(hideIdentityFields && classes.hidden)}>
          <EmailFieldWithSuggestions
            label="Email"
            control={control}
            name="contact.email"
            errors={errors}
            classes={{}}
            disabled={inviteIsValid}
            fullWidth
            required
          />
        </div>
        <PhoneField
          id="contact-phone"
          name="contact.phoneNumber"
          inputRef={register}
          error={!!errors?.contact?.phoneNumber}
          helperText={errors?.contact?.phoneNumber?.message}
          label="Phone number"
          autoComplete="tel"
          className={clsx(
            hidePhone && classes.hidden,
            hideIdentityFields && !hidePhone && classes.fieldFull,
          )}
          fullWidth
          required
        />
      </div>
    </section>
  )
}

export default ContactFields
